import { useEffect, useState } from 'react';
import Card from '../../components/Card.jsx';
import StatCard from '../../components/StatCard.jsx';
import Chip from '../../components/Chip.jsx';
import DataTable from '../../components/DataTable.jsx';
import { msaApi } from '../../lib/apiClient.js';
import { number } from '../../utils/format.js';
import { complexCorporateActions, indexMeta, moverHeading, moverTime } from '../../utils/marketPresentation.js';

const EXCHANGES = ['NGX', 'NSE', 'JSE', 'GSE', 'EGX', 'BRVM'];

export default function Briefings() {
  const [indices, setIndices] = useState(null);
  const [movers, setMovers] = useState(null);
  const [news, setNews] = useState(null);
  const [caCalendar, setCaCalendar] = useState(null);
  const [exchange, setExchange] = useState('NGX');
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const complexAnnouncements = complexCorporateActions(caCalendar?.announcements);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErrors([]);
    Promise.allSettled([
      msaApi.indices(),
      msaApi.movers(exchange),
      msaApi.news(),
      // CA calendar is live from the backend (/api/msa/corporate-actions →
      // MSA /dividends/calendar + /corporate-actions). Live since 2026-08-18
      // (the earlier empty feed was a field-name mapping bug, not an empty
      // MSA feed).
      msaApi.corporateActions(),
    ]).then((results) => {
      if (!alive) return;
      [setIndices, setMovers, setNews, setCaCalendar].forEach((setter, i) => setter(results[i].status === 'fulfilled' ? results[i].value : null));
      setErrors(results.flatMap((result, i) => result.status === 'rejected' ? [`${['Indices', 'Movers', 'News', 'Corporate actions'][i]} feed unavailable.`] : []));
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [exchange]);

  return (
    <>
      <Card>
        <h1 className="heading serif" style={{ fontSize: 24 }}>
          Morning Briefing — African Markets
        </h1>
        <p className="aq-editorial">
          Market-data dashboard sourced from MSA. Each feed reports its own observation time;
          missing or delayed feeds are not a statement about today’s market direction.
        </p>
        {errors.map((error) => <p role="alert" key={error}>{error}</p>)}
      </Card>

      <section>
        <div className="aq-row-head">
          <h2 className="aq-section-title heading">Indices</h2>
          {indices?.retrievedAt && <span className="aq-news-meta">Feeds retrieved {new Date(indices.retrievedAt).toLocaleString()} · each card shows its own observation basis</span>}
        </div>
        <div className="aq-grid aq-grid--4">
          {loading && <Card>Loading…</Card>}
          {!loading &&
            (indices?.items || []).map((idx) => (
              <StatCard
                key={idx.symbol}
                label={idx.name}
                value={number(idx.value)}
                delta={idx.changePct}
                meta={indexMeta(idx)}
              />
            ))}
        </div>
      </section>

      <section>
        <div className="aq-row-head">
          <div>
            <h2 className="aq-section-title heading">
              {moverHeading(movers)}
            </h2>
            {movers?.asOf && <span className="aq-news-meta">Latest quote update {moverTime(movers.asOf, movers.marketStatus)} · {movers.marketStatus?.confidence}</span>}
            {movers?.error && <p role="alert">{movers.error}</p>}
          </div>
          <div className="aq-seg">
            {EXCHANGES.map((ex) => (
              <button
                key={ex}
                className={`aq-seg-btn ${exchange === ex ? 'aq-seg-btn--active' : ''}`}
                onClick={() => setExchange(ex)}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
        <Card>
          <DataTable
            loading={loading}
            emptyLabel={movers?.error || 'No verified gainers for this exchange session.'}
            columns={[
              { key: 'symbol', header: 'Symbol' },
              { key: 'name', header: 'Company' },
              { key: 'last', header: 'Last', numeric: true, render: (r) => number(r.last) },
              { key: 'previousClose', header: 'Previous close', numeric: true, render: (r) => number(r.previousClose) },
              {
                key: 'changePct',
                header: 'Change',
                render: (r) => <Chip tone={r.changePct >= 0 ? 'gain' : 'loss'}>{r.changePct > 0 ? '▲ +' : r.changePct < 0 ? '▼ ' : '— '}{Number(r.changePct).toFixed(2)}%</Chip>,
              },
              { key: 'asOf', header: 'Quote time', render: (r) => moverTime(r.asOf, movers?.marketStatus) },
            ]}
            rows={movers?.items || []}
          />
        </Card>
      </section>

      <section>
        <h2 className="aq-section-title heading">Corporate-action calendar</h2>
        <Card>
          {(() => {
            const today = new Date().toISOString().slice(0, 10);
            const all = caCalendar?.dividends || [];
            const upcoming = all.filter((d) => (d.exDate || d.paymentDate || '') >= today);
            const rows = (upcoming.length ? upcoming : all).slice(0, 20);
            return (
              <>
                <DataTable
                  loading={loading}
                  emptyLabel="No scheduled events for the African universe yet."
                  columns={[
                    { key: 'symbol', header: 'Symbol' },
                    { key: 'exchange', header: 'Exchange' },
                    { key: 'eventType', header: 'Event' },
                    { key: 'exDate', header: 'Ex-date', render: (r) => (r.exDate ? new Date(r.exDate).toLocaleDateString() : '—') },
                    {
                      key: 'amount',
                      header: 'Amount',
                      numeric: true,
                      render: (r) => r.amountDisplay || (r.amount != null ? `${r.amount.toFixed(4)} ${r.currency || ''}` : '—'),
                    },
                    { key: 'paymentDate', header: 'Payment', render: (r) => (r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : '—') },
                    {
                      key: 'flagged',
                      header: 'Status',
                      render: (r) => <Chip tone={r.verified ? 'gain' : 'warn'}>{r.lifecycleStatus || 'Declared'}</Chip>,
                    },
                    { key: 'sourceLabel', header: 'Source' },
                    { key: 'lastVerifiedAt', header: 'Verified', render: (r) => (r.lastVerifiedAt ? new Date(r.lastVerifiedAt).toLocaleDateString() : 'Not verified') },
                  ]}
                  rows={rows}
                />
                {caCalendar?.summary?.dividends > rows.length && (
                  <p className="aq-news-meta">
                    {upcoming.length
                      ? `Showing the next ${rows.length} of ${upcoming.length} upcoming dividend events.`
                      : `No upcoming events; showing the most recent ${rows.length} of ${all.length}.`}
                  </p>
                )}
              </>
            );
          })()}
          {complexAnnouncements.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <h4 className="heading" style={{ fontSize: 14, marginBottom: 6 }}>
                Complex corporate actions flagged ({complexAnnouncements.length})
              </h4>
              <ul className="aq-news-list">
                {complexAnnouncements.map((a) => (
                  <li key={a.id}>
                    <span className="aq-news-headline">
                      {a.symbol ? `${a.symbol} · ` : ''}
                      {a.title}
                    </span>
                    <span className="aq-news-meta">
                      {a.exchange ? `${a.exchange} · ` : ''}
                      {a.eventType}
                      {a.complex && ' · flagged — complex event, adjusted series may carry gaps'}
                      {a.declarationDate && ` · ${new Date(a.declarationDate).toLocaleDateString()}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="aq-note">
            The adjustment basis of the connected legacy price history has not been verified.
            Dividend and corporate-action events shown here must not be assumed to be incorporated into backtest prices.
          </p>
        </Card>
      </section>

      <section>
        <h2 className="aq-section-title heading">News</h2>
        <Card>
          {loading && <div className="aq-empty-state">Loading…</div>}
          {!loading && (news?.items || []).length === 0 && (
            <div className="aq-empty-state">No news right now.</div>
          )}
          <ul className="aq-news-list">
            {(news?.items || []).map((n) => (
              <li key={n.id}>
                <span className="aq-news-headline">{n.headline}</span>
                <span className="aq-news-meta">
                  {n.source} · {new Date(n.publishedAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </section>
    </>
  );
}
